/* Cycling Upgrades — native sell flow (sections/cu-trade-in.liquid). Talks to the
   CRM's public /api/trade-in/* routes at data-api; pure state, validation and the
   submit payload live in cu-sell-core.js (unit-tested). Steps render into one
   stage in the page; key-moment clips play only on the customer's own actions. */
const sellRoot = document.querySelector('[data-cu-sell]');
// The core module's URL comes from asset_url (with Shopify's ?v= cache-buster),
// so a deploy never pairs this file with a stale cached core.
const core = sellRoot ? await import(sellRoot.dataset.core) : null;
if (sellRoot) init(sellRoot);

function init(root) {
  const API = root.dataset.api.replace(/\/$/, '');
  const VIDEOS = JSON.parse(root.dataset.videos || '{}');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stage = root.querySelector('[data-sell-stage]');
  const progress = root.querySelector('[data-sell-progress]');
  const live = root.querySelector('[data-sell-live]');
  const back = root.querySelector('[data-sell-back]');
  const momentWrap = root.querySelector('[data-sell-moment-wrap]');
  const momentVideo = root.querySelector('[data-sell-moment]');

  let config = null;
  let state = core.initialState();
  let errors = {};
  let cashAllowed = false;
  let lastCheckedEmail = '';
  let momentPlaying = false;
  let pendingDraft = null; // an unfinished draft the type screen offers to resume
  const fieldsCache = {}; // product type -> Promise<FieldDef[]>
  const modelsCache = {}; // lowercased product type -> Promise<{ brands, models }>

  /* ---------- helpers ---------- */
  function el(tag, attrs = {}, ...children) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k.startsWith('on')) node.addEventListener(k.slice(2), v);
      else if (k === 'class') node.className = v;
      else if (k === 'text') node.textContent = v;
      else if (k === 'value') node.value = v;
      else node.setAttribute(k, v === true ? '' : v);
    }
    for (const c of children.flat()) if (c != null && c !== false) node.append(c);
    return node;
  }
  const icon = (name) => root.querySelector(`template[data-sell-icon="${name}"]`)?.content.cloneNode(true) ?? '';

  // Each type card shows its piece of the home page's bike drawing
  // (snippets/cu-bike.liquid): which parts, the crop (drawing units) and a line
  // weight that survives the scale-down.
  const TYPE_ART = {
    complete_bike: { parts: null, box: '215 150 980 570', line: 7 },
    frameset: { parts: ['frame', 'fork'], box: '400 205 600 365', line: 5 },
    wheelset: { parts: ['rear-wheel', 'cassette'], box: '225 305 410 410', line: 4.5 },
    misc_parts: { parts: ['cassette', 'rd', 'crankset', 'chain', 'fd'], box: '375 445 345 200', line: 3 },
  };
  function typeArt(type) {
    const art = TYPE_ART[type];
    const svg = root.querySelector('template[data-sell-bike]')?.content.querySelector('svg')?.cloneNode(true);
    if (!art || !svg) return '';
    svg.setAttribute('viewBox', art.box);
    svg.style.setProperty('--line', String(art.line));
    if (art.parts) svg.querySelectorAll('.cu-bk-part').forEach((g) => { if (!art.parts.includes(g.dataset.cuPart)) g.remove(); });
    return el('span', { class: 'cu-sell__art', 'aria-hidden': 'true' }, svg);
  }
  const idFor = (path) => `CuSell-${path.replace(/[^a-z0-9]+/gi, '-')}`;
  const textarea = (value, attrs) => {
    const t = el('textarea', attrs);
    t.value = value || '';
    return t;
  };

  // A labelled control with its hint and error wired through aria-describedby.
  function field(label, path, control, hint) {
    const id = idFor(path);
    control.id = id;
    const err = errors[path];
    const describedBy = [hint && `${id}-hint`, err && `${id}-err`].filter(Boolean).join(' ');
    if (describedBy) control.setAttribute('aria-describedby', describedBy);
    if (err) control.setAttribute('aria-invalid', 'true');
    if (label.endsWith('*')) control.setAttribute('aria-required', 'true');
    return el('div', { class: 'cu-sell__field' },
      el('label', { for: id, text: label }),
      control,
      hint && el('p', { class: 'cu-sell__hint', id: `${id}-hint`, text: hint }),
      err && el('p', { class: 'cu-sell__error', id: `${id}-err`, text: err }));
  }

  async function api(path, body) {
    const res = await fetch(`${API}/api/trade-in/${path}`, body === undefined
      ? {}
      : { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(json.error || String(res.status)), { status: res.status, body: json });
    return json;
  }

  function save() {
    try { localStorage.setItem(core.DRAFT_KEY, core.serializeDraft(state)); } catch { /* storage blocked */ }
  }
  function clearDraft() {
    try { localStorage.removeItem(core.DRAFT_KEY); } catch { /* storage blocked */ }
  }
  function set(patch) {
    state = { ...state, ...patch };
    save();
  }
  // By uid, not index: uploads and vision calls resolve after the customer may
  // have added or removed items.
  function updateItem(uid, patch) {
    set({ items: state.items.map((it) => (it.uid === uid ? { ...it, ...patch } : it)) });
  }
  const itemByUid = (uid) => state.items.find((it) => it.uid === uid);

  /* ---------- key moments ---------- */
  // Plays a short clip over the stage, only in response to the customer's action.
  // Reduced motion: shows the clip's finished still for a beat instead.
  // The overlay eases in, and eases out over whatever renders beneath it next
  // (playMoment resolves as the fade-out starts).
  // Clips play through a canvas sized to the panel: the frame is fitted inside
  // and the rest filled with the panel's slate. Canvas pixels are colour-managed
  // like the page (a <video> element isn't on macOS Chrome), so the clip's slate
  // and the panel's match exactly and there's no visible box.
  const momentCanvas = root.querySelector('[data-sell-moment-canvas]');
  const mctx = momentCanvas.getContext('2d');
  let momentFrame = 0;
  let momentBox = { w: 0, h: 0 };
  function paintMoment(source, w, h) {
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.round(momentCanvas.clientWidth * dpr), ch = Math.round(momentCanvas.clientHeight * dpr);
    if (momentCanvas.width !== cw || momentCanvas.height !== ch) { momentCanvas.width = cw; momentCanvas.height = ch; }
    mctx.fillStyle = getComputedStyle(momentWrap).backgroundColor;
    mctx.fillRect(0, 0, cw, ch);
    if (!w || !h) return;
    const bw = Math.min(cw, momentBox.w * dpr), bh = Math.min(ch, momentBox.h * dpr);
    const k = Math.min(bw / w, bh / h);
    mctx.drawImage(source, (cw - w * k) / 2, (bh - h * k) / 2, w * k, h * k);
  }
  function showMoment() {
    cancelAnimationFrame(momentFrame);
    // The slate covers the whole panel, but the drawing stays fitted to the
    // panel's size at the start, so the next step growing the panel mid-fade
    // never moves or shrinks it.
    momentBox = { w: root.clientWidth, h: root.clientHeight };
    momentWrap.hidden = false;
    paintMoment(null, 0, 0);
    momentWrap.classList.remove('is-on');
    void momentWrap.offsetWidth; // start the fade from transparent
    momentWrap.classList.add('is-on');
  }
  function hideMoment() {
    // Keep painting through the fade-out: the next step renders underneath and
    // can resize the panel, and a canvas that stops repainting gets stretched.
    momentWrap.classList.remove('is-on');
    // Hide once the CSS fade-out finishes (its duration lives in cu.css only).
    const ms = reduceMotion ? 0 : parseFloat(getComputedStyle(momentWrap).transitionDuration) * 1000;
    setTimeout(() => {
      if (momentWrap.classList.contains('is-on')) return;
      cancelAnimationFrame(momentFrame);
      momentWrap.hidden = true;
    }, ms);
  }
  function playMoment(key) {
    const clip = VIDEOS[key];
    if (!clip) return Promise.resolve();
    showMoment();
    if (reduceMotion) {
      const still = new Image();
      still.onload = () => {
        if (!momentWrap.classList.contains('is-on')) return; // the moment already ended
        const loop = () => { paintMoment(still, still.naturalWidth, still.naturalHeight); momentFrame = requestAnimationFrame(loop); };
        loop();
      };
      still.src = clip.still;
      return new Promise((resolve) => setTimeout(() => { hideMoment(); resolve(); }, 600));
    }
    return new Promise((resolve) => {
      let finished = false;
      const done = () => {
        if (finished) return;
        finished = true;
        momentVideo.onended = momentVideo.onerror = null;
        hideMoment();
        resolve();
      };
      // A <source> that fails fires its own error, not the video's; the last one
      // failing means nothing can play.
      momentVideo.replaceChildren(
        el('source', { src: clip.webm, type: 'video/webm' }),
        el('source', { src: clip.mp4, type: 'video/mp4', onerror: () => done() }));
      momentVideo.onended = done;
      momentVideo.onerror = done;
      momentVideo.load();
      const loop = () => {
        if (momentVideo.readyState >= 2) paintMoment(momentVideo, momentVideo.videoWidth, momentVideo.videoHeight);
        else paintMoment(null, 0, 0);
        momentFrame = requestAnimationFrame(loop);
      };
      momentFrame = requestAnimationFrame(loop);
      momentVideo.play().catch(done);
      setTimeout(done, 3500); // never hold the flow on a stalled clip
    });
  }

  /* ---------- navigation ---------- */
  // `enter` animates the step in — only on a step change, never on a re-render
  // within a step (e.g. after a photo uploads).
  function render(enter = false) {
    const activeId = document.activeElement && stage.contains(document.activeElement) ? document.activeElement.id : '';
    const idx = core.STEPS.indexOf(state.step);
    progress.max = core.STEPS.length;
    progress.value = state.step === 'done' ? core.STEPS.length : Math.max(idx, 0);
    back.hidden = idx <= 0;
    const view = el('div', { class: `cu-sell__step${enter ? ' is-enter' : ''}` });
    RENDER[state.step](view);
    stage.replaceChildren(view);
    if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
  }

  function go(step) {
    errors = {};
    set({ step });
    render(true);
    toStepTop();
  }

  // Every step change (and the done screen) brings the top of the sell section
  // into view, so each step starts at its heading rather than wherever the
  // previous step's Continue button left the page.
  function toStepTop() {
    stage.querySelector('h2')?.focus({ preventScroll: true });
    root.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  function showErrors() {
    render();
    const orphans = Object.entries(errors)
      .filter(([k]) => !document.getElementById(idFor(k)) && !document.getElementById(`${idFor(k)}-err`))
      .map(([, m]) => m);
    if (orphans.length) {
      const alert = el('div', { class: 'cu-sell__alert', role: 'alert', tabindex: '-1' }, orphans.join(' '));
      stage.firstElementChild?.querySelector('h2')?.after(alert);
    }
    const n = Object.keys(errors).length;
    live.textContent = `Please fix ${n} ${n === 1 ? 'thing' : 'things'} before continuing.`;
    stage.querySelector('[aria-invalid="true"]')?.focus();
  }

  async function next() {
    if (state.step === 'you') await checkCash(state.seller.email);
    errors = core.validateStep(state, config, state.step, cashAllowed);
    if (Object.keys(errors).length) return showErrors();
    go(core.STEPS[core.STEPS.indexOf(state.step) + 1]);
  }

  back.addEventListener('click', () => {
    const idx = core.STEPS.indexOf(state.step);
    if (idx > 0) go(core.STEPS[idx - 1]);
  });

  const heading = (text, lede) => [
    el('h2', { class: 'cu-sell__title', tabindex: '-1', text }),
    lede && el('p', { class: 'cu-sell__lede', text: lede }),
  ];
  const actions = (...buttons) => el('div', { class: 'cu-sell__actions' }, ...buttons);
  const primary = (text, onclick) => el('button', { type: 'button', class: 'cu-pill', onclick }, text);
  const secondary = (text, onclick) => el('button', { type: 'button', class: 'cu-pill cu-pill--outline', onclick }, text);
  const brandButton = (text, onclick) => el('button', { type: 'button', class: 'cu-pill cu-pill--brand', onclick }, text);
  const spinner = () => el('span', { class: 'cu-spinner', 'aria-hidden': 'true' });
  // Full-panel loader while a slow call runs.
  function busyOverlay(title, text) {
    const busy = el('div', { class: 'cu-sell__busy', role: 'status' },
      el('div', {}, spinner(), el('p', { class: 'cu-sell__title', text: title }), text && el('p', { text })));
    root.append(busy);
    return busy;
  }

  /* ---------- lookups ---------- */
  function modelsFor(type) {
    const k = type.trim().toLowerCase();
    if (!k) return Promise.resolve({ brands: [], models: [] });
    modelsCache[k] ||= api(`models?type=${encodeURIComponent(type.trim())}`).catch(() => ({ brands: [], models: [] }));
    return modelsCache[k];
  }
  function fieldsFor(type) {
    const k = type.trim();
    if (!k) return Promise.resolve([]);
    fieldsCache[k] ||= api(`fields?type=${encodeURIComponent(k)}`).then((r) => r.fields || []).catch(() => []);
    return fieldsCache[k];
  }

  // <input> + <datalist>: suggestions, never a gate (free text is allowed).
  function suggestInput(value, listId, options, attrs) {
    const input = el('input', { type: 'text', list: listId, autocomplete: 'off', value, ...attrs });
    const list = el('datalist', { id: listId }, options.map((o) => el('option', { value: o })));
    return [input, list];
  }

  // Brand and Model inputs suggested from our catalog (GET /models) for the
  // item's product type: brands for the type (config.brands when it has none),
  // models narrowed to the typed brand. Suggestions only — anything typed is
  // kept. Used by the item card and the complete-bike parts rows; call
  // refresh() when the item's type is set or changes.
  function brandModelInputs(uid, { brandAttrs = {}, modelAttrs = {} } = {}) {
    const it = itemByUid(uid);
    const brandListId = `CuSellBrands-${uid}`;
    const modelListId = `CuSellModels-${uid}`;
    const typeOf = () => (itemByUid(uid)?.product_type || '').trim();
    const fillModels = () => modelsFor(typeOf()).then(({ models }) => {
      const brand = (itemByUid(uid)?.brand || '').trim().toLowerCase();
      const names = models.filter((m) => !brand || (m.brand || '').toLowerCase() === brand).map((m) => m.name);
      document.getElementById(modelListId)?.replaceChildren(...names.map((n) => el('option', { value: n })));
    });
    const [brand, brandList] = suggestInput(it.brand, brandListId, [], {
      ...brandAttrs, oninput: (e) => { updateItem(uid, { brand: e.target.value }); fillModels(); },
    });
    const [model, modelList] = suggestInput(it.model, modelListId, [], {
      ...modelAttrs, oninput: (e) => updateItem(uid, { model: e.target.value }),
    });
    function refresh() {
      const type = typeOf();
      if (!type) return;
      modelsFor(type).then(({ brands }) => {
        const list = brands.length ? brands : config.brands;
        document.getElementById(brandListId)?.replaceChildren(...list.map((b) => el('option', { value: b })));
      });
      fillModels();
    }
    return { brand, brandList, model, modelList, refresh };
  }

  /* ---------- photos ---------- */
  function blobBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  async function toBase64Jpeg(file) {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, config.photos.maxEdgePx / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) throw new Error('encode failed');
    return { imageBase64: await blobBase64(blob), mediaType: 'image/jpeg' };
  }
  // Downscale (or, for HEIC this browser can't decode, send as-is) then upload.
  async function uploadFile(file) {
    let image;
    try {
      image = await toBase64Jpeg(file);
    } catch {
      const fallback = core.prepareUploadFallback(file);
      if (!fallback.ok) throw new Error(fallback.message);
      image = { imageBase64: await blobBase64(file), mediaType: fallback.mediaType };
    }
    const { url } = await api('upload', image);
    return { url, image };
  }

  // One photo slot. getPhotos/putPhotos read and write the slot group's CURRENT
  // state, so two uploads finishing together never overwrite each other.
  function photoSlot({ label, path, required, getPhotos, putPhotos, read, write }) {
    const url = read(getPhotos());
    const input = el('input', { type: 'file', accept: config.photos.accept,
      'aria-label': url ? `Replace ${label.toLowerCase()} photo` : `${label} photo${required ? ' (required)' : ' (optional)'}`,
      'aria-required': required && !url ? 'true' : null });
    const err = errors[path];
    if (err) {
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', `${idFor(path)}-err`);
    }
    const slot = el('label', { class: `cu-sell__slot${url ? ' is-filled' : ''}` },
      url ? (core.isPreviewable(url) ? el('img', { src: url, alt: '' }) : el('span', { class: 'cu-sell__hint', text: 'Photo added' })) : icon('upload'),
      el('span', { class: 'cu-sell__slot-label', 'aria-hidden': 'true', text: url ? 'Replace' : `${label}${required ? ' *' : ''}` }),
      input);
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      slot.classList.add('is-busy');
      live.textContent = `Uploading ${label.toLowerCase()} photo…`;
      try {
        const { url: newUrl } = await uploadFile(file);
        putPhotos(write(getPhotos(), newUrl));
        delete errors[path];
        live.textContent = `${label} photo added.`;
        render();
      } catch (e) {
        errors[path] = e.status ? 'Upload failed — choose the photo again to retry.' : e.message || 'Upload failed — choose the photo again to retry.';
        live.textContent = errors[path];
        render();
      }
    });
    return el('div', {}, slot, err && el('p', { class: 'cu-sell__error', id: `${idFor(path)}-err`, text: err }));
  }

  function photoGrid(type, pathBase, getPhotos, putPhotos) {
    const def = core.photoSlots(type);
    const grid = el('div', { class: `cu-sell__photos${def.slots.length === 1 ? ' cu-sell__photos--one' : ''}` });
    for (const s of def.slots) {
      grid.append(photoSlot({ label: s.label, path: `${pathBase}.${s.id}`, required: s.required, getPhotos, putPhotos,
        read: (p) => p?.[s.id], write: (p, u) => ({ ...p, [s.id]: u }) }));
    }
    for (let n = 0; n < def.extras; n++) {
      grid.append(photoSlot({ label: `Extra ${n + 1}`, path: `${pathBase}.extra.${n}`, required: false, getPhotos, putPhotos,
        read: (p) => p?.extra?.[n], write: (p, u) => { const extra = [...(p.extra || [])]; extra[n] = u; return { ...p, extra }; } }));
    }
    return grid;
  }

  function criteria(type) {
    const tpl = root.querySelector(`template[data-sell-acceptance="${type}"]`);
    if (!tpl) return null;
    return el('details', { class: 'cu-sell__criteria' },
      el('summary', { text: 'See our acceptance criteria' }), tpl.content.cloneNode(true));
  }

  function specFields(uid, defs) {
    return defs.map((d) => {
      const it = itemByUid(uid);
      const path = `items.${state.items.findIndex((x) => x.uid === uid)}.metafields.${d.key}`;
      const value = it?.metafields[d.key] ?? '';
      const write = (e) => updateItem(uid, { metafields: { ...itemByUid(uid).metafields, [d.key]: e.target.value } });
      if (d.strict && d.choices) {
        return field(d.label, path, el('select', { onchange: write },
          el('option', { value: '', text: 'Choose…' }),
          d.choices.map((c) => el('option', { value: c, text: c, selected: c === value }))));
      }
      if (d.choices) {
        const [input, list] = suggestInput(value, `${idFor(path)}-list`, d.choices, { oninput: write });
        return el('div', {}, field(d.label, path, input), list);
      }
      return field(d.label, path, el('input', {
        type: d.input === 'number' ? 'number' : 'text', inputmode: d.input === 'number' ? 'numeric' : null, value, oninput: write,
      }));
    });
  }

  function serialField(uid) {
    const key = config.serial.metafieldKey;
    return field('Frame serial number (optional)', `items.0.metafields.${key}`,
      el('input', { type: 'text', value: itemByUid(uid).metafields[key] ?? '',
        oninput: (e) => updateItem(uid, { metafields: { ...itemByUid(uid).metafields, [key]: e.target.value } }) }),
      'Usually stamped under the bottom bracket.');
  }

  /* ---------- steps ---------- */
  function renderType(view) {
    view.append(...heading('What are you selling?', 'Pick one to start — you can add more items later.'));
    // Cash paused: say so before anything is filled in, not at the payout step.
    // The cash allowlist is per email, unknown yet, so this shows to everyone;
    // an allowlisted seller still gets cash at the payout step.
    if (core.cashPaused(config, false)) view.append(el('div', { class: 'cu-sell__alert', role: 'note' }, el('p', { text: config.payout.pausedMessage })));
    if (pendingDraft) {
      const label = config.submissionTypes.find((t) => t.value === pendingDraft.submission_type)?.label || '';
      view.append(el('div', { class: 'cu-sell__alert' },
        el('p', { text: `You have an unfinished ${label.toLowerCase()} submission.` }),
        actions(
          primary('Pick up where I left off', () => {
            state = pendingDraft;
            pendingDraft = null;
            go(state.step === 'type' ? core.AFTER_TYPE : state.step);
          }),
          secondary('Start over', () => { pendingDraft = null; clearDraft(); render(); }))));
    }
    const grid = el('div', { class: 'cu-sell__types', role: 'group', 'aria-label': 'What are you selling?' });
    for (const t of config.submissionTypes) {
      grid.append(el('button', {
        type: 'button', class: 'cu-sell__type', 'aria-pressed': String(state.submission_type === t.value),
        onclick: async () => {
          if (momentPlaying) return;
          momentPlaying = true;
          pendingDraft = null;
          if (state.submission_type !== t.value) {
            state = core.chooseType(state, config, t.value);
          }
          save();
          await playMoment(t.value);
          momentPlaying = false;
          go(core.AFTER_TYPE);
        },
      }, typeArt(t.value), el('span', { class: 'cu-sell__type-label', text: t.label })));
    }
    view.append(grid);
  }

  function renderItem(view) {
    const type = state.submission_type;
    if (type === 'complete_bike') return renderBike(view);
    const label = config.submissionTypes.find((t) => t.value === type)?.label || '';
    view.append(...heading(type === 'misc_parts' ? 'Tell us about your parts' : `Tell us about your ${label.toLowerCase()}`,
      'A few details and photos so we can appraise it accurately.'));
    if (type === 'misc_parts') {
      view.append(el('div', { class: 'cu-sell__alert' }, el('p', {},
        el('strong', { text: 'Selling more than one part? Use this one form for all of them.' }),
        ' Fill in the first item, then tap “Add another item” for each extra part — please don’t submit a separate form per item.')));
    }
    const crit = criteria(type);
    if (crit) view.append(crit);
    state.items.forEach((it, i) => view.append(itemCard(it.uid, i, i === 0 ? type : 'misc_parts')));
    view.append(actions(primary(core.nextLabel('item'), next)));
  }

  function itemCard(uid, i, slotType) {
    const it = itemByUid(uid);
    const fixed = i === 0 ? config.fixedTypes[state.submission_type] : null;
    const left = el('div');
    const right = el('div');
    let typeInput = null;
    const card = el('fieldset', { class: 'cu-sell__item' },
      el('legend', { class: 'cu-sell__item-title', text: `Item ${i + 1}` }),
      el('div', { class: 'cu-sell__split' }, left, right));

    if (!fixed) {
      const [input, list] = suggestInput(it.product_type, `CuSellTypes-${uid}`, config.productTypes, {
        oninput: (e) => updateItem(uid, { product_type: e.target.value }),
        // Refresh only this card's type-dependent parts: a full re-render on
        // blur would swallow a click on the next control (e.g. Add another item).
        onchange: () => loadTypeExtras(),
      });
      left.append(field('What is it? *', `items.${i}.product_type`, input, 'e.g. Saddle, Power Meter, Handlebar'), list);
      typeInput = input;
    }
    // "Groupset" alone: ask disc or rim (core.bareGroupsetChoice).
    const brakesHost = el('div');
    left.append(brakesHost);
    const { brand, brandList, model, modelList, refresh: refreshBrandModel } = brandModelInputs(uid);
    const yearHost = el('div');
    left.append(
      field('Brand', `items.${i}.brand`, brand), brandList,
      field('Model', `items.${i}.model`, model, 'Not in the list? Just type it.'), modelList,
      yearHost,
      field('Key specs', `items.${i}.quick_spec`, el('input', { type: 'text', value: it.quick_spec,
        oninput: (e) => updateItem(uid, { quick_spec: e.target.value }) }), 'Size, length, tooth count — whatever matters most'));
    if (i === 0 && config.serial.submissionTypes.includes(state.submission_type)) left.append(serialField(uid));
    const specsHost = el('div');
    left.append(specsHost);
    left.append(conditionField(`items.${i}.condition_label`, it.condition_label, (v) => updateItem(uid, { condition_label: v })));
    left.append(mileageField(`items.${i}.mileage_claim`, it.mileage_claim, (v) => updateItem(uid, { mileage_claim: v })));
    left.append(field('Notes (optional)', `items.${i}.notes`, textarea(it.notes, {
      placeholder: 'Upgrades, flaws, service history, included extras…', oninput: (e) => updateItem(uid, { notes: e.target.value }) })));
    // Year (framesets only), brand/model suggestions and spec fields for the item's current type.
    function loadTypeExtras() {
      const cur = itemByUid(uid);
      const brakes = core.bareGroupsetChoice(cur, config);
      brakesHost.replaceChildren(...(brakes ? [field(brakes.label, `items.${i}.brakes`,
        el('select', { onchange: (e) => {
          if (!e.target.value) return;
          updateItem(uid, { product_type: e.target.value });
          if (typeInput) typeInput.value = e.target.value;
          loadTypeExtras();
        } },
          el('option', { value: '', text: 'Choose…' }),
          brakes.options.map((o) => el('option', { value: o.value, text: o.label }))),
        'Disc brakes grip a metal rotor at the center of each wheel; rim brakes grip the wheel rim itself.')] : []));
      yearHost.replaceChildren(...(core.asksYear(cur, config) ? [field('Year', `items.${i}.year`, el('input', {
        type: 'number', inputmode: 'numeric', min: String(config.yearRange.min), max: String(config.yearRange.max), value: cur.year ?? '',
        oninput: (e) => updateItem(uid, { year: e.target.value === '' ? null : Number(e.target.value) }) }))] : []));
      const type = (cur?.product_type || '').trim();
      if (!type) { specsHost.replaceChildren(); return; }
      refreshBrandModel();
      fieldsFor(type).then((defs) => {
        if ((itemByUid(uid)?.product_type || '').trim() === type) specsHost.replaceChildren(...specFields(uid, defs));
      });
    }
    loadTypeExtras();
    // Add sits on the last card, beside Remove, so it reads as part of the item list.
    const cardActions = [];
    if (i > 0) {
      cardActions.push(secondary('Remove this item', () => {
        set({ items: state.items.filter((x) => x.uid !== uid) });
        render();
      }));
    }
    if (i === state.items.length - 1) {
      cardActions.push(brandButton('+ Add another item', () => {
        set({ items: [...state.items, core.newItem('', false, 'misc_parts')] });
        render();
        stage.querySelectorAll('.cu-sell__item')[state.items.length - 1]?.querySelector('input')?.focus();
      }));
    }
    if (cardActions.length) left.append(actions(...cardActions));

    right.append(
      el('p', { class: 'cu-sell__legend', text: 'Photos' }),
      el('p', { class: 'cu-sell__hint', text: 'JPEG, PNG or HEIC. Clear, well-lit shots get better offers.' }),
      photoGrid(slotType, `items.${i}.photos`,
        () => itemByUid(uid)?.photos, (photos) => updateItem(uid, { photos })));
    return card;
  }

  function renderBike(view) {
    view.append(...heading('Tell us about your bike', 'A short description and a photo or two, then add what you know about the parts.'));
    const crit = criteria('complete_bike');
    if (crit) view.append(crit);
    const left = el('div');
    left.append(field('Tell us about your bike *', 'description',
      textarea(state.description, { placeholder: 'Year, model, size, upgrades, wear or damage…', oninput: (e) => set({ description: e.target.value }) })));
    if (config.serial.submissionTypes.includes('complete_bike')) left.append(serialField(state.items[0].uid));
    const first = state.items[0];
    left.append(
      conditionField('items.0.condition_label', state.items.find((it) => it.condition_label)?.condition_label || '',
        (v) => set(core.setBikeWide(state, { condition_label: v })), 'Overall condition'),
      mileageField('items.0.mileage_claim', first.mileage_claim, (v) => set(core.setBikeWide(state, { mileage_claim: v }))));
    const right = el('div', {},
      el('p', { class: 'cu-sell__legend', text: 'Photos' }),
      el('p', { class: 'cu-sell__hint', text: 'The whole bike, drive side (non-drive side if you can). JPEG, PNG or HEIC.' }),
      photoGrid('complete_bike', 'bikePhotos', () => state.bikePhotos, (bikePhotos) => set({ bikePhotos })));
    view.append(el('div', { class: 'cu-sell__split' }, left, right), componentRows());
    view.append(actions(primary(core.nextLabel('item'), next)));
  }

  // The complete bike's parts: every row can be retyped (Handlebar → Integrated
  // Handlebar), removed (the Stem that integrated bar replaced) or added to, and
  // Brand / Model suggest from the catalog. The brakes row keeps its Disc / Rim
  // choice. Every field is optional apart from the part type.
  function componentRows() {
    const wrap = el('div', { class: 'cu-sell__rows' },
      el('h3', { class: 'cu-sell__legend', text: 'Your parts — add what you know (all optional)' }));
    if (errors.items) wrap.append(el('p', { class: 'cu-sell__error', text: errors.items }));
    state.items.forEach((it, i) => {
      const alt = Object.entries(config.fullBike.alternates)
        .find(([k, a]) => k === it.product_type || a.options.some((o) => o.value === it.product_type));
      const name = alt ? alt[0].replace(/^Disc Brake /, '') : it.product_type || 'New part';
      const bm = brandModelInputs(it.uid, {
        brandAttrs: { 'aria-label': `${name} brand` }, modelAttrs: { 'aria-label': `${name} model` },
      });
      let typeCell;
      if (alt) {
        typeCell = field(`${name}: ${alt[1].label.toLowerCase()}`, `items.${i}.product_type`,
          el('select', { onchange: (e) => { updateItem(it.uid, { product_type: e.target.value }); bm.refresh(); } },
            alt[1].options.map((o) => el('option', { value: o.value, text: o.label, selected: o.value === it.product_type }))));
      } else {
        const [input, list] = suggestInput(it.product_type, `CuSellTypes-${it.uid}`, config.productTypes, {
          oninput: (e) => updateItem(it.uid, { product_type: e.target.value }),
          onchange: () => bm.refresh(),
        });
        typeCell = el('div', {}, field('Part', `items.${i}.product_type`, input), list);
      }
      const input = (key, label, extra = {}) => field(label, `items.${i}.${key}`, el('input', {
        type: 'text', value: it[key], 'aria-label': `${name} ${label.toLowerCase()}`,
        oninput: (e) => updateItem(it.uid, { [key]: e.target.value }), ...extra,
      }));
      const remove = state.items.length > 1
        ? el('button', { type: 'button', class: 'cu-sell__row-remove', 'aria-label': `Remove ${name}`,
          onclick: () => { set({ items: state.items.filter((x) => x.uid !== it.uid) }); render(); } }, 'Remove')
        : el('span');
      wrap.append(el('div', { class: 'cu-sell__row' }, typeCell,
        el('div', {}, field('Brand', `items.${i}.brand`, bm.brand), bm.brandList),
        el('div', {}, field('Model', `items.${i}.model`, bm.model), bm.modelList),
        input('quick_spec', 'Specs', { placeholder: config.fullBike.specHints[it.product_type] || '' }), remove));
      bm.refresh();
    });
    wrap.append(actions(brandButton('+ Add another part', () => {
      set({ items: [...state.items, core.newItem('', false, 'none')] });
      render();
      stage.querySelectorAll('.cu-sell__row')[state.items.length - 1]?.querySelector('input')?.focus();
    })));
    return wrap;
  }

  function conditionField(path, value, onPick, label = 'Condition') {
    return field(label, path, el('select', { onchange: (e) => onPick(e.target.value) },
      el('option', { value: '', text: 'Choose…' }),
      config.conditions.map((c) => el('option', { value: c, text: c, selected: c === value }))));
  }
  function mileageField(path, value, onInput) {
    return field('Mileage (optional)', path, el('input', { type: 'text', value, maxlength: '200', oninput: (e) => onInput(e.target.value) }),
      'Roughly how far it’s been ridden, e.g. 2,000 miles');
  }

  // Whether cash is open to this email while cash offers are paused. Kept as a
  // promise so Review waits for an in-flight check instead of using a stale answer.
  let cashCheck = Promise.resolve();
  function checkCash(email) {
    if (config.payout.cashOffersEnabled) return cashCheck;
    const e = email.trim();
    if (e === lastCheckedEmail) return cashCheck;
    lastCheckedEmail = e;
    cashCheck = api('cash-eligibility', { email: e })
      .then((r) => !!r.cashAllowed, () => false)
      .then((allowed) => {
        if (lastCheckedEmail !== e) return; // a newer email is being checked
        const before = state.seller.payout_preference;
        const changed = allowed !== cashAllowed;
        cashAllowed = allowed;
        state = core.applyCashEligibility(state, config, cashAllowed);
        save();
        const relocked = before !== state.seller.payout_preference;
        if (relocked) live.textContent = 'Cash isn’t available for this email — your payout is set to store credit.';
        // Re-render only when the payout options changed: a needless re-render
        // between mousedown and mouseup would swallow the click being made.
        if (changed || relocked) render();
      });
    return cashCheck;
  }

  function renderYou(view) {
    const s = state.seller;
    const setSeller = (k) => (e) => set({ seller: { ...state.seller, [k]: e.target.value } });
    view.append(...heading('About you and your payout', 'We’ll email your offer — usually within one business day.'));
    view.append(el('div', { class: 'cu-sell__split' },
      el('div', {},
        field('First name *', 'seller.first_name', el('input', { type: 'text', autocomplete: 'given-name', value: s.first_name, oninput: setSeller('first_name') })),
        field('Last name', 'seller.last_name', el('input', { type: 'text', autocomplete: 'family-name', value: s.last_name, oninput: setSeller('last_name') }))),
      el('div', {},
        field('Email *', 'seller.email', el('input', { type: 'email', autocomplete: 'email', value: s.email, oninput: setSeller('email'), onblur: (e) => checkCash(e.target.value) })),
        field('Phone', 'seller.phone', el('input', { type: 'tel', autocomplete: 'tel', inputmode: 'tel', value: s.phone, maxlength: '30',
          oninput: setSeller('phone'),
          onblur: (e) => { e.target.value = core.formatPhone(e.target.value); setSeller('phone')(e); } })))));
    view.append(field('Where are you shipping from? *', 'seller.location', el('select', { onchange: setSeller('location') },
      el('option', { value: '', text: 'Choose…' }),
      config.locations.map((o) => el('option', { value: o.value, text: o.label, selected: o.value === s.location })))));

    const payoutErr = errors['seller.payout_preference'];
    const payoutErrId = `${idFor('seller.payout_preference')}-err`;
    const group = el('fieldset', { class: 'cu-sell__choices', role: 'radiogroup', 'aria-required': 'true', 'aria-describedby': payoutErr ? payoutErrId : null },
      el('legend', { class: 'cu-sell__legend', text: 'How would you like to be paid? *' }));
    for (const o of core.payoutOptions(config, cashAllowed)) {
      group.append(el('label', { class: 'cu-sell__choice' },
        el('input', { type: 'radio', name: 'payout', id: `CuSellPayout-${o.value}`, value: o.value, checked: o.value === s.payout_preference, disabled: o.disabled,
          'aria-invalid': payoutErr ? 'true' : null,
          onchange: () => { set({ seller: { ...state.seller, payout_preference: o.value } }); render(); } }),
        el('span', { text: o.label })));
    }
    view.append(group);
    if (core.cashPaused(config, cashAllowed)) view.append(el('p', { class: 'cu-sell__hint', text: config.payout.pausedMessage }));
    if (payoutErr) view.append(el('p', { class: 'cu-sell__error', id: payoutErrId, text: payoutErr }));
    if (core.cashPaused(config, cashAllowed)) {
      const ackErr = errors['seller.credit_ack'];
      const ackErrId = `${idFor('seller.credit_ack')}-err`;
      view.append(el('label', { class: 'cu-sell__ack' },
        el('input', { type: 'checkbox', id: idFor('seller.credit_ack'), checked: !!s.credit_ack, 'aria-required': 'true',
          'aria-invalid': ackErr ? 'true' : null, 'aria-describedby': ackErr ? ackErrId : null,
          onchange: (e) => { set({ seller: { ...state.seller, credit_ack: e.target.checked } }); render(); } }),
        el('span', { text: 'I understand my offer will be in store credit, not cash.' })));
      if (ackErr) view.append(el('p', { class: 'cu-sell__error', id: ackErrId, text: ackErr }));
    }
    if (s.payout_preference === 'store_credit' || s.payout_preference === 'both') {
      view.append(field('Which item are you interested in? (optional)', 'seller.interested_in',
        textarea(s.interested_in, { maxlength: '500', rows: '3', oninput: setSeller('interested_in') }),
        'Paste a link, product title or SKU from our store — we’ll put your credit toward it.'));
    }
    view.append(field('Anything else we should know? (optional)', 'additional_info',
      textarea(state.additional_info, { maxlength: '2000', oninput: (e) => set({ additional_info: e.target.value }) }),
      'Custom builds, how you’d like to be paid, timing — anything that helps us make the offer.'));
    // Honeypot: hidden from people and assistive tech; bots fill it.
    view.append(el('div', { 'aria-hidden': 'true', style: 'position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden' },
      el('label', { for: 'CuSellWebsite', text: 'Website' }),
      el('input', { id: 'CuSellWebsite', type: 'text', tabindex: '-1', autocomplete: 'off', value: state.website, oninput: (e) => set({ website: e.target.value }) })));
    view.append(actions(primary(core.nextLabel('you'), next)));
  }

  function renderReview(view) {
    const p = core.buildPayload(state);
    const isBike = p.submission_type === 'complete_bike';
    const labelOf = (list, v) => list.find((o) => o.value === v)?.label;
    const section = (title, step, rows) => el('section', {},
      el('h3', {}, title, secondary(`Edit ${title.toLowerCase()}`, () => go(step))),
      el('dl', {}, rows.flatMap(([k, v]) => (v ? [el('dt', { text: k }), el('dd', { text: v })] : []))));
    const itemRows = p.items
      .filter((it) => !isBike || it.brand || it.model || it.quick_spec)
      .map((it) => [it.product_type || 'Item', [
        [it.year, it.brand, it.model, it.quick_spec].filter(Boolean).join(' · ') || 'No details yet',
        ...(isBike ? [] : [it.condition_label, it.mileage_claim, it.notes]),
      ].filter(Boolean).join(' — ')]);
    view.append(...heading('Check and send', 'Make sure everything looks right.'));
    view.append(el('div', { class: 'cu-sell__review' },
      section('You', 'you', [
        ['Name', `${p.first_name} ${p.last_name}`.trim()], ['Email', p.email], ['Phone', p.phone],
        ['Shipping from', labelOf(config.locations, p.location)], ['Payout', labelOf(config.payouts, p.payout_preference)],
        ['Interested in', p.interested_in], ['Anything else', p.additional_info],
      ]),
      section('Your item', 'item', [
        ['Selling', labelOf(config.submissionTypes, p.submission_type)],
        ['About it', p.description],
        ...(isBike ? [['Condition', p.items.find((it) => it.condition_label)?.condition_label], ['Mileage', p.items[0]?.mileage_claim]] : []),
        ...itemRows,
        ['Photos', String(p.items.reduce((n, it) => n + it.photo_urls.length, 0))],
      ])));
    const status = el('p', { class: 'cu-sell__error', role: 'alert' });
    view.append(status, actions(primary('Send for an offer', (e) => submit(e.currentTarget, status))));
  }

  async function submit(button, status) {
    button.disabled = true;
    status.textContent = '';
    const busy = busyOverlay('Getting your offer ready…', 'This can take a few seconds — please don’t close or refresh.');
    const payload = core.buildPayload(state);
    try {
      const { deal_number } = await api('submit', payload);
      busy.remove();
      clearDraft();
      await playMoment('received');
      // Not saved: a finished submission must not come back as a draft.
      state = { ...core.initialState(), step: 'done', dealNumber: deal_number, email: payload.email };
      errors = {};
      render();
      toStepTop();
    } catch (err) {
      busy.remove();
      button.disabled = false;
      const issue = err.status === 400 ? core.firstIssue(err.body) : null;
      if (issue) {
        go(core.stepForIssue(issue.path));
        errors = { [core.errorKeyForIssue(issue.path)]: issue.message };
        showErrors();
      } else {
        status.textContent = 'Something went wrong sending your submission — please try again. Your answers are saved.';
      }
    }
  }

  function renderDone(view) {
    view.classList.add('cu-sell__done');
    view.append(
      el('h2', { class: 'cu-sell__title', tabindex: '-1', text: 'Submission received' }),
      el('p', { class: 'cu-sell__deal', text: `#${state.dealNumber}` }),
      el('p', { text: `We’ve emailed a confirmation to ${state.email}. Expect your offer within one business day.` }),
      actions(primary('Sell something else', () => {
        state = core.initialState();
        go('type');
      })));
  }

  const RENDER = {
    type: renderType, item: renderItem, you: renderYou, review: renderReview, done: renderDone,
  };

  /* ---------- boot ---------- */
  (async () => {
    try {
      config = await api('config');
    } catch {
      stage.replaceChildren(el('p', { class: 'cu-sell__alert' },
        'The quote form is unavailable right now. ',
        el('a', { href: root.dataset.fallback, text: 'Open the quote form' }), ' instead.'));
      return;
    }
    let raw = null;
    try { raw = localStorage.getItem(core.DRAFT_KEY); } catch { /* storage blocked */ }
    // Always open on the type screen. A saved draft is only offered back (never
    // auto-restored) and only when it holds real input — restoring the bare
    // "picked a type" state is what opened the flow on a stale Wheelset step.
    const draft = core.restoreDraft(raw);
    if (core.draftHasProgress(draft)) {
      pendingDraft = draft;
      if (draft.seller.email) checkCash(draft.seller.email);
    } else clearDraft();
    render();
  })();
}
